<template>
  <div class="choice-toggle">
    <!-- O que se escolhe, em cima (pedido do usuário: "Cardinalidade", "Obrigatoriedade"…) — as opções dizem
         as respostas, e não a pergunta. O grupo de botões não é um campo do Vuetify, e não desenha rótulo -->
    <label v-if="label" class="ontos-field-label">{{ label }}</label>
    <!-- O arranjo do Sim/Não do item (`BooleanInput`): botões divididos, com ícone, o escolhido destacado.
         `mandatory`: uma opção está sempre escolhida -->
    <v-btn-toggle :model-value="modelValue" mandatory divided density="comfortable" variant="outlined"
                  :disabled="disabled" @update:model-value="$emit('update:modelValue', $event)">
      <v-btn v-for="option in options" :key="String(option.value)" :value="option.value" :color="option.color"
             :class="{'choice-toggle-conditional': option.conditional}">
        <v-icon v-if="option.icon" start>{{ option.icon }}</v-icon>
        <!-- Em duas linhas, quando há o que qualifica: "Obrigatório quando" numa linha não cabia ao lado das
             outras opções, e o grupo ganhava barra de rolagem (relato do usuário) -->
        <span v-if="option.qualifier" class="choice-toggle-two-lines">
          <span>{{ option.label }}</span><span>{{ option.qualifier }}</span>
        </span>
        <template v-else>{{ option.label }}</template>
      </v-btn>
    </v-btn-toggle>
    <!-- A frase da opção escolhida: o que ela faz (pedido do usuário: "gosto de frase explicativa"). Travado,
         a frase é o porquê, à vista: só no passar do mouse, ninguém o achava (relato do usuário: "por que
         não pode mudar de preenchido para calculado?") -->
    <div v-if="(disabled && disabledReason) || (chosen && chosen.hint)" class="choice-toggle-hint text-body-2 text-medium-emphasis">
      <template v-if="disabled && disabledReason"><v-icon size="small" start>mdi-lock</v-icon>{{ disabledReason }}</template>
      <template v-else>{{ chosen!.hint }}</template>
    </div>
    <!-- O que só vale com a opção escolhida — o mínimo e o máximo do "Vários valores", a fórmula do
         "Obrigatório quando". Quem decide se há é quem usa, pelo slot -->
    <div v-if="$slots.default" class="choice-toggle-more"><slot/></div>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'

/**
 * Uma opção: o valor, o rótulo do botão, o ícone e a cor (as da marca, quando há) e a frase do que ela faz.
 * `conditional`: a que vale só às vezes — o "… quando" —, escolhida contornada, e não preenchida, como o selo
 * dela na ficha (`PropertyMarks`; pedido do usuário: as duas iguais na janela não se distinguiam).
 * `qualifier`: a segunda linha do rótulo — o "quando" embaixo do "Obrigatório".
 */
export type ChoiceOption<T = unknown> = {
  value: T, label: string, qualifier?: string, hint?: string, icon?: string, color?: string, conditional?: boolean
}

/**
 * Uma escolha entre poucas opções ditas por extenso — "Opcional · Obrigatório · Obrigatório quando" —, no
 * lugar da caixa de marcar (ideia do usuário): os botões do Sim/Não do item, e embaixo a frase da escolhida.
 *
 * As opções por extenso dizem mais que um rótulo e uma caixa: quem lê vê também o que vale quando NÃO
 * escolhe. Travado (`disabled`), diz o porquê no lugar da frase (`disabledReason`).
 *
 * O par de dois lados (`PairToggle`) é este com duas opções; o modo do valor padrão também é este.
 */
@Component({name: 'ontos-choice-toggle', emits: ['update:modelValue']})
class ChoiceToggle extends Vue {
  @Prop({default: undefined}) readonly modelValue!: unknown
  @Prop({type: String, default: ''}) readonly label!: string
  @Prop({type: Array, required: true}) readonly options!: ChoiceOption[]
  @Prop({type: Boolean, default: false}) readonly disabled!: boolean
  @Prop({type: String, default: ''}) readonly disabledReason!: string

  get chosen(): ChoiceOption | undefined {
    return this.options.find(option => option.value === this.modelValue)
  }
}

export default toNative(ChoiceToggle)
</script>

<style scoped>
.choice-toggle-hint {
  margin-top: 4px;
}

/* O rótulo em duas linhas, na altura do botão: as linhas mais juntas que as do texto corrido */
.choice-toggle-two-lines {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}

/* A que vale só às vezes, escolhida: a borda na cor dela, sem o fundo — o contorno do selo da ficha */
.choice-toggle-conditional.v-btn--active {
  box-shadow: inset 0 0 0 1px currentColor;
}

.choice-toggle-conditional.v-btn--active :deep(.v-btn__overlay) {
  opacity: 0;
}

/* O que depende da opção escolhida, recuado embaixo da frase */
.choice-toggle-more {
  margin-top: 8px;
  padding-left: 16px;
}
</style>
